/**
 * TDBProductBostonMatrix — Kasavana-Smith / Boston-style classifier
 * for TDB Shopify products. Renders a 2×2 quadrant grid:
 *
 *   ┌─────────────────────────┬─────────────────────────┐
 *   │ ⭐ STARS                │ ❓ PUZZLES              │
 *   │ high units · high price │ low units · high price  │
 *   │                         │                         │
 *   ├─────────────────────────┼─────────────────────────┤
 *   │ 🐄 CASH COWS            │ 🐕 DOGS                 │
 *   │ high units · low price  │ low units · low price   │
 *   │                         │                         │
 *   └─────────────────────────┴─────────────────────────┘
 *
 * Period toggle: 7d · 30d · 90d · 365d.
 *
 * Data source: GET /api/tdb/product-matrix?window_days=<N>
 *
 * Y-axis auto-upgrades from revenue-per-unit (price tier proxy)
 * to true contribution-per-unit (price − food_cost) once BOMs are
 * set on ≥20% of trading products. `margin_axis_ready` flag in the
 * payload tells the UI which axis it's looking at; the tile renders
 * a small chip saying so.
 *
 * Lives on TDB DivisionDetail, below TDBLeaderPulse.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Star, DollarSign, HelpCircle, Trash2, Award, ChevronDown, ChevronUp, RefreshCw, Info } from 'lucide-react';

const WINDOWS = [
  { value: 7,   label: '7d' },
  { value: 30,  label: '30d' },
  { value: 90,  label: '90d' },
  { value: 365, label: '1y' },
];

const QUADRANTS = [
  { key: 'stars',     title: 'Stars',       sub: 'High volume × high price · keep front-of-house, never discount', icon: Star,       color: '#0F766E', bg: '#E8F7F4', border: '#BAE7DC' },
  { key: 'puzzles',   title: 'Puzzles',     sub: 'Premium but slow · push via marketing or reposition',           icon: HelpCircle, color: '#92400E', bg: '#FEF6E4', border: '#FACC15' },
  { key: 'cash_cows', title: 'Cash Cows',   sub: 'High volume × low price · price-up cautiously, bundle to lift', icon: DollarSign, color: '#185FA5', bg: '#EEF4FA', border: '#B5D4F4' },
  { key: 'dogs',      title: 'Dogs',        sub: 'Slow + cheap · delist, repackage, or kill',                     icon: Trash2,     color: '#A32D2D', bg: '#FCEBEB', border: '#E8B4B4' },
];

const fINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

export default function TDBProductBostonMatrix() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/tdb/product-matrix?window_days=${windowDays}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  if (loading && !data) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2 text-xs text-[#9CA3AF]"
        style={{ borderColor: '#E5E1DC' }} data-testid="tdb-boston-matrix-loading">
        Building Boston matrix from {windowDays}d of Shopify orders…
      </div>
    );
  }
  if (data?.forbidden) return null;
  if (error) {
    return (
      <div className="rounded-md px-3 py-2 text-xs"
        style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}
        data-testid="tdb-boston-matrix-error">
        Boston matrix error: {error}
      </div>
    );
  }
  if (!data) return null;

  if (!data.available) {
    return (
      <Section title="Product Boston Matrix" testid="tdb-boston-matrix-empty">
        <p className="text-xs italic text-[#9CA3AF]">{data.note || 'No paid Shopify orders in this window.'}</p>
        <WindowToggle value={windowDays} onChange={setWindowDays} />
      </Section>
    );
  }

  return (
    <div className="rounded-[10px] bg-white border p-4" style={{ borderColor: '#E5E1DC' }}
      data-testid="tdb-boston-matrix">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Award size={16} style={{ color: '#185FA5' }} />
          <h3 className="text-sm font-semibold text-[#1B2A4A]" data-testid="boston-matrix-title">
            TDB · Product Boston Matrix
          </h3>
          <span className="text-[10px] text-[#9CA3AF]">
            {data.window_start} → {data.window_end} · {data.totals.unique_products} products · {fINR(data.totals.total_revenue)} revenue
          </span>
        </div>
        <div className="flex items-center gap-2">
          <WindowToggle value={windowDays} onChange={setWindowDays} />
          <button onClick={() => { setRefreshing(true); load(true); }} disabled={refreshing}
            className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border hover:bg-[#F7F5F2] disabled:opacity-50"
            style={{ borderColor: '#E5E1DC', color: '#185FA5' }}
            data-testid="boston-matrix-refresh">
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Axis info banner */}
      <div className="rounded-md px-3 py-1.5 mb-3 flex items-start gap-2 text-[11px]"
        style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }} data-testid="boston-matrix-axes-banner">
        <Info size={11} className="mt-0.5 flex-shrink-0" />
        <span>
          X-axis: <strong>{data.axes.x_label}</strong> (cutoff: {data.axes.x_cutoff} units at the {data.axes.x_cutoff_percentile}th percentile) ·
          Y-axis: <strong>{data.axes.y_label}</strong> (cutoff: ₹{data.axes.y_cutoff} at the {data.axes.y_cutoff_percentile}th percentile)
          {!data.margin_axis_ready && (
            <span className="block mt-0.5 italic text-[#92400E]">
              Margin axis activates at 20% BOM coverage (currently {data.margin_coverage_pct}% — {data.margin_coverage_count} of {data.totals.unique_products} products have recipes).
            </span>
          )}
        </span>
      </div>

      {/* 2×2 quadrant grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="boston-matrix-grid">
        {QUADRANTS.map((q) => {
          const items = data.quadrants[q.key] || [];
          const total = data.quadrant_counts[q.key] || 0;
          const visible = expanded[q.key] ? items : items.slice(0, 6);
          const Icon = q.icon;
          return (
            <div key={q.key}
              className="rounded-md p-3"
              style={{ backgroundColor: q.bg, border: `1.5px solid ${q.border}` }}
              data-testid={`boston-quadrant-${q.key}`}>
              <div className="flex items-baseline justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Icon size={14} style={{ color: q.color }} />
                  <h4 className="text-sm font-bold" style={{ color: q.color }}>{q.title}</h4>
                  <span className="text-[11px] font-medium" style={{ color: q.color, opacity: 0.85 }}>
                    {total}
                  </span>
                </div>
              </div>
              <p className="text-[10px] mb-2" style={{ color: q.color, opacity: 0.85 }}>{q.sub}</p>

              {items.length === 0 ? (
                <p className="text-[11px] italic text-[#9CA3AF]">No products in this quadrant.</p>
              ) : (
                <>
                  <ul className="space-y-1" data-testid={`boston-quadrant-list-${q.key}`}>
                    {visible.map((p, i) => (
                      <li key={`${p.name}-${i}`}
                        className="flex items-center justify-between gap-2 text-[11px] py-0.5">
                        <span className="truncate flex-1 text-[#1B2A4A]" title={p.name}>
                          {i + 1}. {p.name}
                        </span>
                        <span className="tabular-nums text-[#6B7280] flex-shrink-0 flex items-center gap-1.5">
                          <span>{p.units}× · {fINR(p.revenue)} · ₹{p.avg_price}/u</span>
                          {p.margin_pct != null && (
                            <span className="px-1 py-0.5 rounded text-[9px] font-semibold"
                              style={{
                                backgroundColor: p.margin_pct >= 70 ? '#E8F7F4' : p.margin_pct >= 50 ? '#FEF6E4' : '#FCEBEB',
                                color: p.margin_pct >= 70 ? '#0F766E' : p.margin_pct >= 50 ? '#92400E' : '#A32D2D',
                              }}
                              title={p.recipe_source ? `Recipe: ${p.recipe_source} · cost ₹${p.recipe_cost}` : 'Recipe data'}>
                              {p.margin_pct}%
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {items.length > 6 && (
                    <button onClick={() => toggle(q.key)}
                      className="text-[11px] mt-1.5 flex items-center gap-1 hover:underline"
                      style={{ color: q.color }}
                      data-testid={`boston-quadrant-expand-${q.key}`}>
                      {expanded[q.key]
                        ? <><ChevronUp size={11} /> Show top 6</>
                        : <><ChevronDown size={11} /> Show all {items.length}</>}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals strip */}
      <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]"
        style={{ borderColor: '#F0EBE5' }}
        data-testid="boston-matrix-totals">
        <Stat label="Products" value={data.totals.unique_products} />
        <Stat label="Units sold" value={data.totals.total_units} />
        <Stat label="Revenue" value={fINR(data.totals.total_revenue)} />
        <Stat label="Line items" value={data.totals.total_orders} />
      </div>
    </div>
  );
}

function WindowToggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-0.5 rounded-full bg-[#F7F5F2] p-0.5">
      {WINDOWS.map((w) => (
        <button key={w.value} onClick={() => onChange(w.value)}
          className={`px-2 py-0.5 rounded-full text-[11px] ${value === w.value ? 'font-semibold text-white' : 'text-[#6B7280] hover:bg-white'}`}
          style={value === w.value ? { backgroundColor: '#185FA5' } : {}}
          data-testid={`boston-window-${w.value}`}>
          {w.label}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest font-semibold text-[#6B7280]">{label}</p>
      <p className="text-sm font-bold tabular-nums text-[#1B2A4A]">{value}</p>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div className="rounded-[10px] bg-white border p-3" style={{ borderColor: '#E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold text-[#1B2A4A] mb-2">{title}</h3>
      {children}
    </div>
  );
}
